import type { QueryClient, QueryKey } from '@tanstack/react-query';

// The platform operator uses a separate cookie and API from a tenant. These
// are the top-level keys used by the operator pages, including Security's
// historical standalone keys.
const ADMIN_QUERY_ROOTS = new Set(['admin', 'admin-security', 'admin-trusted-devices', 'admin-sessions']);

export function isAdminQueryKey(key: QueryKey): boolean {
  return typeof key[0] === 'string' && ADMIN_QUERY_ROOTS.has(key[0]);
}

/**
 * `keepRoot` spares one tenant query by its top-level key: the session, which
 * every caller has just set to null. Removing it instead makes the next render
 * rebuild it with no data and ask the server again — and a signed-out tab that
 * asks gets a 401, which ends the session in every other tab once more.
 */
export function clearTenantQueries(client: QueryClient, keepRoot?: string): void {
  client.removeQueries({
    predicate: (query) =>
      !isAdminQueryKey(query.queryKey) && (keepRoot === undefined || query.queryKey[0] !== keepRoot),
  });
  // A tenant mutation can retain its variables or result after a sign-out.
  client.getMutationCache().clear();
}

export function clearAdminQueries(client: QueryClient, keepMe = false): void {
  client.removeQueries({
    predicate: (query) =>
      isAdminQueryKey(query.queryKey) && (!keepMe || query.queryKey[0] !== 'admin' || query.queryKey[1] !== 'me'),
  });
}
